import { useEffect, useRef, useCallback, useReducer } from 'react';
import { ChartData } from '../../../api';
import { chartsService } from '../../../api/chartsService';
import { publicDashboardsService } from '../../../api/publicDashboardsService';
import { sharedDashboardsService } from '../../../api/sharedDashboardsService';
import { getCachedChartData, setCachedChartData } from './chartDataCache';

/**
 * Per-chart data fetching hook — loads chart data only when the chart
 * becomes visible (via `isVisible`), filters are ready (`filtersReady`),
 * and refetches when filters change.
 *
 * ## Filters-before-charts gate
 *
 * The `filtersReady` flag is controlled by the parent `Dashboard` component.
 * It becomes `true` once every dashboard filter has finished loading its
 * options (or errored). This prevents charts from fetching with stale/empty
 * filter state and then immediately re-fetching when filters settle.
 *
 * **Cache reads are NOT gated** — if the chart data cache already has an
 * entry for the current filters key, it renders on the first frame
 * regardless of `filtersReady`. Only API calls are deferred.
 *
 * Uses the global `chartDataCache` so that:
 *   - Navigating between dashboards reuses cached data (instant render).
 *   - Entering edit mode reads from cache (no duplicate fetch).
 *   - Saving in edit mode writes to cache (return to dashboard is instant).
 *
 * Lifecycle:
 *   isVisible=false        → idle (no fetch, cache read on init)
 *   filtersReady=false     → idle (no fetch, cache read on init)
 *   isVisible+filtersReady → check cache → hit = render, miss = fetch → cache + render
 *   filters change         → different cache key → miss → fetch
 *
 * Trend data (KPI sparkline) is part of ChartData.trend — no separate state.
 */

// ── State ──────────────────────────────────────────────────────────────────

interface ChartDataState {
  data: ChartData | null;
  isLoading: boolean;
  error: string | null;
}

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; data: ChartData }
  | { type: 'FETCH_ERROR'; error: string }
  | { type: 'RESET' };

function reducer(state: ChartDataState, action: Action): ChartDataState {
  switch (action.type) {
    case 'FETCH_START':
      // Keep previous data visible while loading to prevent chart unmount/flicker.
      return { data: state.data, isLoading: true, error: null };
    case 'FETCH_SUCCESS':
      // Bail out when the data reference hasn't changed — prevents
      // unnecessary re-renders when cached data is re-dispatched.
      if (state.data === action.data && !state.isLoading && !state.error) {
        return state;
      }
      return { data: action.data, isLoading: false, error: null };
    case 'FETCH_ERROR':
      return { data: null, isLoading: false, error: action.error };
    case 'RESET':
      return { data: null, isLoading: false, error: null };
  }
}

const INITIAL_STATE: ChartDataState = { data: null, isLoading: false, error: null };

// ── Hook ───────────────────────────────────────────────────────────────────

export function useChartData(
  dashboardId: string | undefined,
  chartId: string,
  filters: Record<string, any>,
  isVisible: boolean,
  filtersReady: boolean,
  recompile: boolean = false,
  shareToken?: string,
  sharedDashboardId?: string,
): ChartDataState {
  const filtersKey = JSON.stringify(filters);

  // Lazy initializer — reads cache synchronously so the very first render
  // already has data when available.  No spinner flash on cache hit.
  const [state, dispatch] = useReducer(reducer, undefined, () => {
    if (dashboardId) {
      const cached = getCachedChartData(dashboardId, chartId, filtersKey);
      if (cached) {
        return { data: cached.data, isLoading: false, error: null };
      }
    }
    return INITIAL_STATE;
  });

  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!dashboardId) return;

    // Cancel any in-flight request
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    const requestId = ++requestIdRef.current;
    dispatch({ type: 'FETCH_START' });

    try {
      const response = shareToken
        ? await publicDashboardsService.getChartData(shareToken, chartId, filters)
        : sharedDashboardId
          ? await sharedDashboardsService.getChartData(sharedDashboardId, chartId, filters)
          : await chartsService.getChartData(chartId, { filters, refresh: false, recompile });

      // Guard against stale responses
      if (requestId !== requestIdRef.current) return;

      // Write to shared cache (trend is inside data.trend now)
      setCachedChartData(dashboardId, chartId, filtersKey, response.data);
      dispatch({ type: 'FETCH_SUCCESS', data: response.data });
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return;
      if (err?.name === 'AbortError') return;

      console.error(`Failed to load chart ${chartId}:`, err);
      dispatch({ type: 'FETCH_ERROR', error: 'Failed to load chart data' });
    }
  }, [dashboardId, chartId, filtersKey, filters, recompile, shareToken, sharedDashboardId]);

  useEffect(() => {
    if (!dashboardId) return;

    // Check shared cache first — cache reads are never gated by filtersReady
    // or visibility, so cached charts render instantly on revisit.
    const cached = getCachedChartData(dashboardId, chartId, filtersKey);
    if (cached) {
      dispatch({ type: 'FETCH_SUCCESS', data: cached.data });
      return;
    }

    // Cache miss — only fetch if visible AND filters are ready
    if (!isVisible) return;
    if (!filtersReady) return;

    fetchData();
  }, [isVisible, filtersReady, dashboardId, chartId, filtersKey, fetchData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  return state;
}